import React, { useState, useEffect } from 'react';
import { Crown, Check, Globe, Mail, Palette, ArrowRight, Loader2 } from 'lucide-react';
import api from '../utils/api';

/**
 * WhiteLabelOrderBump Component
 * 
 * Displays after successful payment to offer the White Label add-on.
 * Matches the dark gradient style of the signup success page.
 * 
 * Props:
 * - billingCycle: "monthly" | "annual" - determines pricing shown
 * - onAccept: () => void - called when user clicks "Add White Label"
 * - onDecline: () => void - called when user clicks "No thanks"
 * - loading: boolean - shows loading state on accept button
 */
const WhiteLabelOrderBump = ({ billingCycle = 'monthly', onAccept, onDecline, loading = false }) => {
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);

  useEffect(() => {
    fetchPricing();
  }, [billingCycle]);

  const fetchPricing = async () => {
    try {
      setLoadingPricing(true);
      const token = localStorage.getItem('access_token');
      const response = await api.get('/billing/white-label/pricing', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.data.success) {
        setPricing(response.data.pricing);
      }
    } catch (err) {
      console.error('Failed to fetch white label pricing:', err);
      // Use fallback pricing
      setPricing({
        billing_cycle: billingCycle,
        price: billingCycle === 'annual' ? 926.64 : 99,
        monthly_price: 99,
        annual_price: 926.64,
        monthly_equivalent: billingCycle === 'annual' ? 77.22 : 99,
        annual_savings: billingCycle === 'annual' ? 261.36 : 0,
        discount_percent: 22
      });
    } finally {
      setLoadingPricing(false);
    }
  };

  const benefits = [
    {
      icon: <Globe className="w-5 h-5" />,
      title: "Custom Email Domain",
      description: "Send emails from your own domain (e.g., notifications@yourcompany.com)"
    },
    {
      icon: <Mail className="w-5 h-5" />,
      title: "Branded Email Notifications",
      description: "Your logo, colors, and company name on all client emails"
    },
    {
      icon: <Palette className="w-5 h-5" />,
      title: "White-Labeled Client Portal",
      description: "Remove Athena branding from your client-facing portal"
    }
  ];

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: price % 1 === 0 ? 0 : 2
    }).format(price);
  };

  if (loadingPricing) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/30">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
        </div>
      </div>
    );
  }

  const isAnnual = pricing?.billing_cycle === 'annual';
  const displayPrice = pricing?.price || (isAnnual ? 926.64 : 99);
  const monthlyEquivalent = pricing?.monthly_equivalent || (isAnnual ? 77.22 : 99);
  const savings = pricing?.annual_savings || (isAnnual ? 261.36 : 0);

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-yellow-500/30 relative overflow-hidden">
      {/* Premium Badge */}
      <div className="absolute top-4 right-4">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full text-white text-xs font-semibold shadow-lg">
          <Crown className="w-3.5 h-3.5" />
          <span>EXCLUSIVE OFFER</span>
        </div>
      </div>

      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Crown className="w-8 h-8 text-yellow-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Upgrade to White Label
        </h2>
        <p className="text-purple-200">
          Make Athena completely yours with custom branding
        </p>
      </div>

      {/* Benefits */}
      <div className="space-y-4 mb-8">
        {benefits.map((benefit, index) => (
          <div key={index} className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
            <div className="flex-shrink-0 w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400">
              {benefit.icon}
            </div>
            <div>
              <h3 className="text-white font-semibold mb-1">{benefit.title}</h3>
              <p className="text-purple-300 text-sm">{benefit.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Pricing */}
      <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-3xl font-bold text-white">
              {formatPrice(displayPrice)}
              <span className="text-lg font-normal text-purple-200">
                /{isAnnual ? 'year' : 'month'}
              </span>
            </div>
            {isAnnual && (
              <div className="text-purple-300 text-sm mt-1">
                That&apos;s just {formatPrice(monthlyEquivalent)}/month
              </div>
            )}
          </div>
          {isAnnual && savings > 0 && (
            <div className="text-right">
              <div className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-semibold">
                Save {formatPrice(savings)}
              </div>
              <div className="text-purple-300 text-xs mt-1">
                22% annual discount
              </div>
            </div>
          )}
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-purple-200">
            <Check className="w-4 h-4 text-green-400" />
            <span>Same billing cycle as your subscription</span>
          </div>
          <div className="flex items-center gap-2 text-purple-200">
            <Check className="w-4 h-4 text-green-400" />
            <span>Cancel anytime from settings</span>
          </div>
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="space-y-4">
        <button
          onClick={onAccept}
          disabled={loading}
          className="w-full py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-orange-500/25"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <Crown className="w-5 h-5" />
              <span>Add White Label - {formatPrice(displayPrice)}</span>
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
        
        <button
          onClick={onDecline}
          disabled={loading}
          className="w-full py-3 text-purple-300 hover:text-white transition-colors text-sm"
        >
          No thanks, continue without White Label
        </button>
      </div>

      {/* Trust Badge */}
      <p className="text-center text-purple-400/60 text-xs mt-6">
        Secure checkout powered by Stripe. You can add White Label later from your settings.
      </p>
    </div>
  );
};

export default WhiteLabelOrderBump;
